import React, { useEffect, useState } from 'react';
import useStyles from 'isomorphic-style-loader/useStyles';

import { Summary, Button, IllustrationStreak } from '@sololearnorg/sol';
import {
	IStreakGoalConfig,
	IStreakGoalOption,
	i18n,
} from '../../../symphony';

import { useFadeOut } from '../SlHooks/useFadeOut';
import { CalendarIcon } from './CalendarIcon';
import { StreakGoalOption } from '../Streak/StreakGoalOption/StreakGoalOption';
import { SlTransition } from '../SlTransition/SlTransition';

import s from './SlStreakGoalScreen.scss';

export interface ISlStreakGoalScreenProps {
    goalConfig: IStreakGoalConfig;
    onClick: (value: number) => void;
}

export const SlStreakGoalScreen: React.FC<ISlStreakGoalScreenProps> = React.memo(
	({
		goalConfig: { defaultFooter: footer, options: initialOptions },
		onClick,
	}) => {
		useStyles(s);
		const [option, setOption] = useState<IStreakGoalOption>({
			footer,
		});
		const [options, setOptions] = useState<IStreakGoalOption[]>(initialOptions);

		const { isFadeOutStarted, startFadeOut } = useFadeOut(600);

		const onOptionClick = (selectedOption: IStreakGoalOption): void => {
			if (selectedOption.daysAmount !== option.daysAmount) {
				setOptions(
					[...options].map((o) => ({
						...o,
						selected: o.order === selectedOption.order,
					})),
				);
				setOption(selectedOption);
			}
		};

		const handleSubmit = () => {
			startFadeOut(() => onClick(option.daysAmount));
		};

		const enterKeyHandler = (e: KeyboardEvent) => {
			if (
				!!option.daysAmount
				&& (e.code.toLowerCase() === 'enter'
					|| e.code.toLowerCase() === 'numpadenter')
			) {
				handleSubmit();
			}
		};

		useEffect(() => {
			document.addEventListener('keypress', enterKeyHandler);
			return () => {
				document.removeEventListener('keypress', enterKeyHandler);
			};
		}, [option.daysAmount]);

		return (
			<div
				className="streak-goal-screen"
				sl-test-data="cmpStreakGoalScreen"
			>
				<Summary
					illustration={(
						<SlTransition
							fadeIn={{ delay: 0.1, duration: 0.3 }}
							fadeOut={{ delay: 0, duration: 0.3 }}
							isFadeOutStarted={isFadeOutStarted}
						>
							<IllustrationStreak />
						</SlTransition>
					)}
					headline={(
						<SlTransition
							fadeIn={{ delay: 0.3, duration: 0.3 }}
							fadeOut={{ delay: 0, duration: 0.3 }}
							isFadeOutStarted={isFadeOutStarted}
						>
							<p>{i18n.t('streak.header')}</p>
						</SlTransition>
					)}
					description={(
						<div className="streak-goal-screen__container">
							<div className="streak-goal-screen__content">
								{options.map((o, i) => (
									<SlTransition
										fadeIn={{
											delay: (5 + i) / 10,
											duration: 0.3,
										}}
										fadeOut={{
											delay: 0.1,
											duration: 0.3,
										}}
										isFadeOutStarted={isFadeOutStarted}
										key={o.order}
									>
										<StreakGoalOption
											text={o.text}
											selected={o.selected}
											daysAmount={o.daysAmount}
											onClick={() => onOptionClick(o)}
										/>
									</SlTransition>
								))}
								<SlTransition
									fadeIn={{ delay: 1, duration: 0.3 }}
									fadeOut={{ delay: 0.2, duration: 0.3 }}
									isFadeOutStarted={isFadeOutStarted}
								>
									<div className="streak-goal-screen__footer">
										<div className="streak-goal-screen__footer__icon">
											<CalendarIcon />
										</div>
										<div className="streak-goal-screen__footer__text">
											{i18n.t(
												'streak.default-footer',
											)}
										</div>
									</div>
								</SlTransition>
							</div>
						</div>
					)}
					actionBar
					actions={[
						<SlTransition
							fadeIn={{ delay: 1.2, duration: 0.3 }}
							fadeOut={{ delay: 0.3, duration: 0.3 }}
							isFadeOutStarted={isFadeOutStarted}
							key={1}
						>
							<Button
								key="Continue"
								disabled={!option.daysAmount}
								onClick={handleSubmit}
							>
								{i18n.t('streak.cta')}
							</Button>
						</SlTransition>,
					]}
				/>
			</div>
		);
	},
);
